import { memo, useCallback, useContext, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useLanguage } from 'assets/languages';
import { GuidelineRuleDtoEnums } from 'api/repositories/guidelineRules/guidelineRules.types';
import { GUIDELINE_RULE_DTO_ENUMS } from 'api/repositories/guidelineRules/guidelineRules.constants';
import { isJapanService } from 'constants/domains/domains';
import GUIDELINE_RULES from 'constants/guidelineRules';
import CampaignFormField from '../../../../../../../campaignFormFieldGroup/components/campaignFormField/campaignFormField';
import { CampaignFormStepTwoContext } from '../../../../../../campaignFormStepTwo.context';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';

type PreferredVideoType = {
  name: string;
  value: GuidelineRuleDtoEnums['PREFERRED_VIDEO_TYPE_OPTIONS'] | null;
  url: string | null;
};

const PreferredVideoTypeField = () => {
  const language = useLanguage();
  const languageTexts = {
    ...language.pages.campaigns,
  };
  const { products, guidelineRuleFormMethods } = useContext(CampaignFormContext);
  const { baseGuidelineRules } = useContext(CampaignFormStepTwoContext);

  const preferredVideoTypeValue = useWatch({
    control: guidelineRuleFormMethods.control,
    name: 'PREFERRED_VIDEO_TYPE.value',
  });

  const preferredVideoTypeOptions: PreferredVideoType[] = useMemo(() => {
    return baseGuidelineRules?.reduce<PreferredVideoType[]>(
      (acc, baseGuidelineRule) => {
        if (baseGuidelineRule.groupName === GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PREFERRED_VIDEO_TYPE) {
          let url = null;

          switch (baseGuidelineRule.value) {
            case GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.EMOTIONAL: {
              url = isJapanService()
                ? 'https://www.instagram.com/reel/DNmrgkqxXhZ/?igsh=MWJyeHJqemR6MnNjZw%3D%3D'
                : 'https://www.instagram.com/reel/DATJGOCSg1F';
              break;
            }
            case GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.INFOMATIVE: {
              url = isJapanService()
                ? 'https://www.instagram.com/reel/DMPvDPoTCPz/?igsh=MTl4aXBwZTFxaDM4MA%3D%3D'
                : 'https://bit.ly/42b2yQO';
              break;
            }
            case GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.INFOMATIVE: {
              url = isJapanService()
                ? 'https://www.instagram.com/reel/DMPvDPoTCPz/?igsh=MTl4aXBwZTFxaDM4MA%3D%3D'
                : 'https://bit.ly/42b2yQO';
              break;
            }
            case GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.VISITING: {
              url = isJapanService()
                ? 'https://www.instagram.com/reel/DNh-FjFSP-p/?igsh=MXRzd3lua2k1eXljYg%3D%3D'
                : 'https://bit.ly/4ccdDpD';
              break;
            }
            case GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.OY_VISITING: {
              url = isJapanService()
                ? 'https://www.instagram.com/reel/DKYt1fpyf5w'
                : 'https://www.instagram.com/reel/DKYt1fpyf5w';
              break;
            }
            case GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.OY_PRODUCT_ONLY: {
              url = isJapanService()
                ? 'https://www.instagram.com/reel/DKTomJep_w5'
                : 'https://www.instagram.com/reel/DKTomJep_w5';
              break;
            }
          }

          acc.push({
            name: GUIDELINE_RULES.GetOptionTranslationMap(
              baseGuidelineRule.groupName,
              baseGuidelineRule.value as string,
            ),
            value: baseGuidelineRule.value as GuidelineRuleDtoEnums['PREFERRED_VIDEO_TYPE_OPTIONS'],
            url,
          });
        }
        return acc;
      },
      [
        // // 특별히 없음(미선택)
        // {
        //   name: languageTexts.create.components.preferredVideoTypeSelector.btn_no_preferred_video_type,
        //   value: null,
        //   url: null,
        // },
      ],
    );
  }, [baseGuidelineRules]);

  const commonBeautyPreferredVideoTypeOptions = useMemo(() => {
    return (
      preferredVideoTypeOptions?.filter(({ value }) =>
        [
          null, // @note 실수 아님 삭제하지 말 것
          GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.EMOTIONAL,
          GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.INFOMATIVE,
          GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.VISITING,
        ].includes(value as any),
      ) || []
    );
  }, [preferredVideoTypeOptions]);

  const oliveYoungPreferredVideoTypeOptions = useMemo(() => {
    return (
      preferredVideoTypeOptions?.filter(({ value }) =>
        [
          GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.OY_PRODUCT_ONLY,
          GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.OY_VISITING,
        ].includes(value as any),
      ) || []
    );
  }, [preferredVideoTypeOptions]);

  const onChange = useCallback((value: PreferredVideoType['value']) => {
    guidelineRuleFormMethods.setValue('PREFERRED_VIDEO_TYPE.value', value);
    if (
      value !== GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.OY_PRODUCT_ONLY &&
      value !== GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.OY_VISITING
    ) {
      guidelineRuleFormMethods.setValue(GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.EMBLEM_OLIVEYOUNG, []);
    }
  }, []);

  return (
    <CampaignFormField
      // wrapperClassName="!mt-[50px]"
      as="div"
      label={languageTexts.create_reels_form_preferred_video_type}
      labelClassName="!text-[17px]"
      description={languageTexts.create_reels_form_preferred_video_type_desc}
    >
      <ul className="flex flex-col">
        {products?.map((product) => (
          <li key={product.id} className="text-[13px] whitespace-pre-wrap">
            {preferredVideoTypeValue && (
              <>
                [
                {GUIDELINE_RULES.GetOptionTranslationMap(
                  GUIDELINE_RULES.GROUP_NAMES.PREFERRED_VIDEO_TYPE,
                  preferredVideoTypeValue as PreferredVideoType['value'],
                )}
                ]&nbsp;
              </>
            )}
            {product.name}
          </li>
        ))}
      </ul>
      <ul className="flex flex-row w-full">
        {commonBeautyPreferredVideoTypeOptions.map(({ name, value, url }) => (
          <li
            key={`preferred-video-type-${value}`}
            className="flex flex-col w-1/3 max-w-[33.33%] items-center gap-[6px]"
          >
            <button
              type="button"
              className={`py-[16px] w-full border hover:opacity-70 text-[15px] text-center cursor-pointer select-none ${
                preferredVideoTypeValue === value
                  ? 'border-[#000]'
                  : '[&:not(:first-child)]:border-l-transparent border-[#E8E8E8]'
              }`}
              onClick={() => onChange(value)}
            >
              {name}
            </button>
            {url && (
              <a
                className="text-[13px] text-[#000] underline hover:opacity-70"
                href={url}
                target="_blank"
                rel="noreferrer"
              >
                {languageTexts.create.components.preferredVideoTypeSelector.btn_show_example}
              </a>
            )}
          </li>
        ))}
      </ul>

      {/* 임시기능_2511-2512: 올리브영 캠페인용 */}
      {oliveYoungPreferredVideoTypeOptions.length > 0 && (
        <ul className="mt-[10px] flex flex-row w-full">
          {oliveYoungPreferredVideoTypeOptions?.map(({ name, value, url }) => (
            <li
              key={`preferred-video-type-${value}`}
              className="flex flex-col w-2/4 max-w-[50%] items-center gap-[6px]"
            >
              <button
                type="button"
                className={`py-[16px] w-full border hover:opacity-70 text-[15px] text-center cursor-pointer select-none ${
                  preferredVideoTypeValue === value
                    ? 'border-[#000]'
                    : '[&:not(:first-child)]:border-l-transparent border-[#E8E8E8]'
                }`}
                onClick={() => onChange(value)}
              >
                {value === GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.OY_VISITING
                  ? languageTexts.create.components.preferredVideoTypeSelector.btn_oy_visiting
                  : value === GUIDELINE_RULE_DTO_ENUMS.PREFERRED_VIDEO_TYPE_OPTIONS.OY_PRODUCT_ONLY
                  ? languageTexts.create.components.preferredVideoTypeSelector.btn_oy_product_only
                  : name}
              </button>
              {url && (
                <a
                  className="text-[13px] text-[#000] underline hover:opacity-70"
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {languageTexts.create.components.preferredVideoTypeSelector.btn_show_example}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </CampaignFormField>
  );
};

export default memo(PreferredVideoTypeField);
